import {
  connectAuthEmulator,
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';

let auth: Auth;

export function initAuth() {
  const getEnv = (): Record<string, any> => {
    // Under plain Node/tsx (no Vite), `import.meta` exists but `.env` is
    // `undefined` rather than throwing — an explicit falsy check is required
    // to actually fall through; try/catch around the access never fires.
    const metaEnv = (import.meta as any).env;
    if (metaEnv) return metaEnv;
    return (globalThis as { process?: { env?: Record<string, unknown> } }).process?.env ?? {};
  };

  const env = getEnv();

  const firebaseApp = initializeApp({
    projectId: env.VITE_FIREBASE_PROJECT_ID as string,
    apiKey: env.VITE_FIREBASE_API_KEY as string,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET as string,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
    appId: env.VITE_FIREBASE_APP_ID as string,
  });

  auth = getAuth(firebaseApp);

  try {
    if ((import.meta as any).env.DEV) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    }
  } catch {
    // Not in a Vite environment
  }

  return auth;
}

export function getAuthInstance(): Auth {
  if (!auth) {
    throw new Error('Auth not initialized. Call initAuth() first.');
  }
  return auth;
}

export function signUpWithEmail(email: string, password: string) {
  return createUserWithEmailAndPassword(getAuthInstance(), email, password);
}

export function signInWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(getAuthInstance(), email, password);
}

export function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/calendar');
  return signInWithPopup(getAuthInstance(), provider);
}

export function logout() {
  return signOut(getAuthInstance());
}

export function onAuthChange(callback: (user: FirebaseUser | null) => void) {
  return onAuthStateChanged(getAuthInstance(), callback);
}
